<template>
  <div
    class="content-card list-shell"
    :class="{
      'list-shell--embedded': embedded && !standalone,
      'list-shell--standalone': standalone
    }"
  >
    <div v-if="$slots.banner || $slots.tabs || $slots.filter" class="top-area">
      <slot name="banner" />
      <slot name="tabs" />
      <slot name="filter" />
    </div>
    <div class="action-row">
      <slot name="actions" />
    </div>
    <div ref="tableAreaRef" class="table-area" :class="{ scroll: useScroll }">
      <div class="table-area__inner">
        <slot name="table" :table-height="tableHeight" />
      </div>
    </div>
    <div class="pager-row" v-if="showPager">
      <slot name="pager">
        <el-pagination
          background
          layout="prev, pager, next, sizes, total"
          :total="page.total"
          :current-page="page.current"
          :page-size="page.size"
          :page-sizes="pageSizes"
          @current-change="(c) => $emit('page-change', c)"
          @size-change="(s) => $emit('size-change', s)"
        />
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref } from 'vue'
import { LIST_TABLE_HEIGHT_KEY } from '@/composables/useListTableHeight'

const props = defineProps({
  page: {
    type: Object,
    default: () => ({ current: 1, size: 10, total: 0 })
  },
  pageSizes: {
    type: Array,
    default: () => [10, 20, 50, 100]
  },
  useScroll: {
    type: Boolean,
    default: true
  },
  minTableWidth: {
    type: String,
    default: '1400px'
  },
  embedded: {
    type: Boolean,
    default: true
  },
  standalone: {
    type: Boolean,
    default: false
  }
})

const showPager = computed(() => (props.page?.total ?? 0) >= 0)

const tableAreaRef = ref(null)
const tableHeight = ref(480)

const syncTableHeight = () => {
  const area = tableAreaRef.value
  if (!area) return
  const h = area.clientHeight
  if (h > 0) tableHeight.value = Math.max(200, Math.floor(h))
}

let resizeObserver
onMounted(() => {
  nextTick(() => {
    syncTableHeight()
    resizeObserver = new ResizeObserver(syncTableHeight)
    const area = tableAreaRef.value
    const shell = area?.closest('.list-shell')
    if (area) resizeObserver.observe(area)
    if (shell) resizeObserver.observe(shell)
    window.addEventListener('resize', syncTableHeight)
  })
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('resize', syncTableHeight)
})

provide(LIST_TABLE_HEIGHT_KEY, tableHeight)
</script>

<style scoped>
.list-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: auto;
  min-height: 0;
  background: var(--bymy-color-bg-card);
  border: none;
  border-radius: var(--bymy-radius-md);
  padding: var(--bymy-gap-md);
  box-shadow: none;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;
}
.list-shell--embedded {
  height: 100%;
  min-height: 0;
  max-height: 100%;
  flex: 1;
  margin: 0;
  overflow: hidden;
}
.list-shell--standalone {
  height: auto;
  min-height: var(--bymy-page-min-height);
  flex: none;
  overflow: visible;
}
.list-shell--standalone .table-area {
  flex: none;
  min-height: 240px;
}
.list-shell--standalone .table-area__inner {
  min-height: 240px;
}
.top-area {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  background: var(--bymy-color-bg-card);
}
.action-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
  background: var(--bymy-color-bg-card);
  padding: 0;
}
.table-area {
  flex: 0 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
  position: relative;
}
.table-area.scroll {
  overflow: hidden;
}
.table-area__inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.list-shell--embedded .table-area {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
}
.list-shell--embedded .pager-row {
  flex: 0 0 auto;
}
.table-area :deep(.el-table__fixed-right) {
  box-shadow: -6px 0 12px rgba(0, 0, 0, 0.06);
}
.table-area :deep(.el-table__fixed-left) {
  box-shadow: 6px 0 12px rgba(0, 0, 0, 0.06);
}
.table-area :deep(th.op-col),
.table-area :deep(td.op-col) {
  position: sticky;
  right: 0;
  background: #fff;
  z-index: 3;
  box-shadow: -6px 0 12px rgba(0, 0, 0, 0.08);
}
.pager-row {
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  flex-shrink: 0;
  padding: 8px 12px 6px 0;
  min-width: 0;
  background: var(--bymy-color-bg-card);
  border-top: 1px solid var(--bymy-color-border);
}
</style>
